import { useCallback, useMemo, useRef, useState } from 'react';
import { useLoaderData, useLocation } from 'react-router';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import DetailModules from '~/modules/page-builder/component/detail/detail-modules/DetailModules';
import { GalleryViewToggle } from './GalleryViewToggle';
import type { GalleryView } from './GalleryViewToggle';
import { ProjectImageGrid } from './ProjectImageGrid';
import { collectProjectImages } from './collect-project-images';
import { collectProjectVideos } from './collect-project-videos';
import { ProjectGalleryVideo } from './ProjectGalleryVideo';
import { ProjectPhotoViewerProvider } from './project-photo-viewer-context';
import type { ProjectPhotoViewer } from './project-photo-viewer-context';
import { DetailImageOverlay } from '~/ui-rocco/pages/detail-shared';
import { ProductTaggingContextProvider } from '~/modules/product-tagging/context';
import { ModulesContextProvider } from '~/modules/page-builder/context/modules-context';
import { PaintPickerContextProvider } from '@modules/paint-picker-new/context/PaintPickerContext';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import getModuleWithAds from '@modules/project/util/get-module-with-ads';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    type IslandMode,
    useIslandSection,
    useScrolledPast,
} from '~/ui-rocco/pages/detail-shared';
import {
    PROJECT_MODULE_DARK_TEXT,
    PROJECT_MODULE_GRID,
} from '../project-module-theme';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';

/**
 * P7 — the project image gallery. Reskins the existing page-builder renderer
 * (`DetailModules`) on the dark editorial background: the same module layouts,
 * product tags (desktop pins / mobile under-image drawer), video modules and
 * ad injection as the legacy page, but without the "Project Gallery" heading
 * so the images flow edge-to-edge.
 */
export const ProjectGallerySection = () => {
    // Preview-merged project so BM template-preview drafts render the edited
    // modules/banner; the rest stays on the published loader data.
    const project = useProjectPreviewRouteData();
    const { memberPermissions, memberRecentlyTagged, moduleMeta, ads } =
        useLoaderData<typeof loader>();
    const { desktop: isDesktop, mobile: isMobile } = useAppDisplayModeContext();
    const location = useLocation();
    const modulesContainerRef = useRef<HTMLDivElement>(null);

    const hideAd = location.state?.hideAd;
    const adsMemoized = useLocationMemo(
        hideAd || typeof document === 'undefined' ? [] : ads,
    );
    const modulesWithAds = useMemo(
        () => getModuleWithAds(project.Modules, adsMemoized),
        [project.Modules, adsMemoized],
    );

    // Magic Island: while the reader scrolls through the page-builder blocks
    // (past 30% of the page), hand the island the page's primary CTA — Book a
    // meeting, or the enquiry when Book is not offered to this visitor.
    const { ctas } = usePageCtas();
    const bookCta = useBookCta({ fixedLabel: 'Book a meeting' });
    const enquireCta = useEnquireCta();

    /*
     * The practice's site, for the image viewer's bottom-left "Visit site" and
     * for the grid's hover panels. The project's own untracked website wins
     * where it has one — a project built for a client can point somewhere the
     * practice's own site does not.
     */
    const galleryVisitWebsite = project.Professional?.Title
        ? (() => {
              const website =
                  project.WebsiteUntracked || project.Professional.Website;
              return website
                  ? {
                        website,
                        libraryItemID: project.ID,
                        professionalID: project.Professional.ID,
                        professionalTitle: project.Professional.Title,
                    }
                  : null;
          })()
        : null;
    const scrolledIntoGallery = useScrolledPast(0.3);
    const primaryIsBook = ctas.primary === 'book';
    const openBook = bookCta.open;
    const openEnquire = enquireCta.open;
    const primaryLabel = primaryIsBook ? bookCta.label : enquireCta.label;
    const bookMeetingMode = useMemo<IslandMode>(
        () => ({
            kind: 'cta',
            label: `Like this Project? ${primaryLabel}`,
            onClick: primaryIsBook ? openBook : () => openEnquire(),
        }),
        [primaryIsBook, primaryLabel, openBook, openEnquire],
    );
    const islandRef = useIslandSection<HTMLElement>(bookMeetingMode, {
        enabled: scrolledIntoGallery && ctas.primary !== null,
    });
    // The section root already carries the PaintPicker container ref; fan the
    // node out to both that ref and the island's measured-element callback.
    const setSectionRef = useCallback(
        (node: HTMLElement | null) => {
            // modulesContainerRef is a RefObject (readonly `current`); the
            // section is its only owner, so write through a mutable view.
            (
                modulesContainerRef as { current: HTMLDivElement | null }
            ).current = node as HTMLDivElement | null;
            islandRef(node);
        },
        [islandRef],
    );

    const [view, setView] = useState<GalleryView>('gallery');
    // The same walk the About image uses for its viewer, so both read the
    // project's photographs from one place.
    const gridSlides = useMemo(() => {
        const slides = collectProjectImages(project.Modules, project.Title);
        // The films go back where the page has them rather than after every
        // photograph: the grid is the project's contact sheet, and a sheet
        // that reorders the project is telling a different story. Inserted
        // back to front so an earlier splice cannot shift a later index.
        const films = collectProjectVideos(project.Modules, project.Title);
        for (const film of [...films].reverse()) {
            slides.splice(Math.min(film.insertAt, slides.length), 0, {
                src: film.src,
                alt: film.alt,
                video: (
                    <ProjectGalleryVideo url={film.videoUrl} title={film.alt} />
                ),
            });
        }
        return slides;
    }, [project.Modules, project.Title]);

    // The gallery-view modules draw the same photographs the grid does, so
    // clicking one opens the same viewer on the same slide. The modules are
    // arbitrary CMS markup several layers down, so they reach this through a
    // context rather than a prop — see ProjectPhotoViewerProvider.
    const [modulePhotoIndex, setModulePhotoIndex] = useState<number | null>(
        null,
    );
    const photoViewer = useMemo<ProjectPhotoViewer>(
        () => ({
            openPhotoById: (imageID) => {
                const index = gridSlides.findIndex(
                    (slide) => slide.id === imageID,
                );
                if (index < 0) return false;
                setModulePhotoIndex(index);
                return true;
            },
        }),
        [gridSlides],
    );

    if (!project.Modules?.length) return null;

    const { canCreateTag, canDeleteTag } = memberPermissions;

    return (
        <PaintPickerContextProvider
            containerRef={modulesContainerRef}
            projectId={project.ID}
            projectLink={project.Link}
            bannerUrl={project.Banner?.imageUrl}
            isDesktop={isDesktop}
            isEnabled={false}
        >
            <ProductTaggingContextProvider
                canCreateTag={isDesktop && canCreateTag}
                canDeleteTag={canDeleteTag}
                recentlyTaggedProducts={memberRecentlyTagged.products}
                recentlyTaggedProfessionals={memberRecentlyTagged.professionals}
            >
                <ModulesContextProvider meta={moduleMeta}>
                    <ProjectPhotoViewerProvider value={photoViewer}>
                        <section
                            ref={setSectionRef}
                            id="project-gallery"
                            // All module text sits on the dark editorial background,
                            // so force it white — headings, paragraphs and quotes
                            // render their rich text in divs/headings (with their own
                            // dark text-[#161616]), and the HTML module is arbitrary
                            // CMS markup, so covering p alone left those dark. Cover
                            // the text-bearing elements (links stay the muted grey;
                            // spans inherit, so link labels keep that grey too).
                            // Paragraphs/captions/links also take the body font;
                            // captions render the muted #cbcbcb from the Figma spec;
                            // headings keep their display font. The quote module's
                            // top/bottom rules use the light-theme body colour, so
                            // re-tint them to the section's white/20 hairline. The
                            // treatment stops at product-tagging chrome — see
                            // PROJECT_MODULE_DARK_TEXT. Scoped to this section so the
                            // legacy/light pages are untouched.
                            className={`-scroll-mt-32 bg-transparent md:px-[3rem] ${PROJECT_MODULE_DARK_TEXT}`}
                        >
                            <div className={CONTENT_CAP_PADDED}>
                                {/* Above the modules because it governs all of
                                them. Only offered when there is a grid worth
                                switching to. */}
                                {gridSlides.length > 1 ? (
                                    <div className="max-md:mb-8 md:mb-[3.12rem]">
                                        <GalleryViewToggle
                                            view={view}
                                            onChange={setView}
                                        />
                                    </div>
                                ) : null}

                                {view === 'grid' ? (
                                    <ProjectImageGrid
                                        slides={gridSlides}
                                        projectId={project.ID}
                                        title={project.Title}
                                        professional={
                                            project.Professional?.Title
                                                ? {
                                                      id: project.Professional
                                                          .ID,
                                                      title: project
                                                          .Professional.Title,
                                                      link: project.Professional
                                                          .Link,
                                                      website:
                                                          project.WebsiteUntracked ||
                                                          project.Professional
                                                              .Website,
                                                  }
                                                : undefined
                                        }
                                    />
                                ) : (
                                    <DetailModules
                                        isMobile={isMobile}
                                        modules={modulesWithAds}
                                        isProjectRevamp
                                        // Captioned modules also zero their legacy
                                        // 125px caption bottom margin
                                        // (DetailSingleImageShell.style) so this
                                        // gap is the whole inter-module space.
                                        className={PROJECT_MODULE_GRID}
                                    />
                                )}
                                {modulePhotoIndex !== null ? (
                                    <DetailImageOverlay
                                        slides={gridSlides}
                                        index={modulePhotoIndex}
                                        onIndexChange={setModulePhotoIndex}
                                        onClose={() =>
                                            setModulePhotoIndex(null)
                                        }
                                        pinId={project.ID}
                                        pinType="ProjectPin"
                                        title={project.Title}
                                        visitWebsite={galleryVisitWebsite}
                                    />
                                ) : null}
                            </div>
                        </section>
                    </ProjectPhotoViewerProvider>
                </ModulesContextProvider>
            </ProductTaggingContextProvider>
        </PaintPickerContextProvider>
    );
};
